import { Injectable } from '@angular/core';
import { concatMap } from 'rxjs/operators';
import { SignatureFieldContext } from '../../interfaces/signature';
import { UidGenerator } from '../../utils/uidGenerator';
import { ContentControlMonitor } from '../document-monitors/content-control-monitor';
import { ContentControlToSignatureFieldService } from './content-control-to-signature-field.service';

@Injectable({ providedIn: 'root' })
export class SignatureControllerService {
  constructor(
    private contentControlMonitor: ContentControlMonitor,
    private contentControlToSignatureFieldService: ContentControlToSignatureFieldService
  ) {
    this.subscribeToContentControlDeletedEvents();
  }

  async insert(wordContext: Word.RequestContext, signatureFieldContext: SignatureFieldContext): Promise<void> {
    let contentControlId = signatureFieldContext.contentControlId;
    if (!contentControlId) {
      const contentControl = await this.buildContentControl(wordContext, signatureFieldContext);
      contentControlId = contentControl.id;
    }
    await this.contentControlToSignatureFieldService.set(contentControlId, signatureFieldContext.providerName, signatureFieldContext.field);
  }

  // todo: if we want to insert block level content (like html) this will need to be updated inline with the ContentControlService
  private async buildContentControl(
    wordContext: Word.RequestContext,
    signatureFieldContext: SignatureFieldContext
  ): Promise<Word.ContentControl> {
    await wordContext.sync();

    const contentControlRange = wordContext.document.getSelection();

    const title = `${signatureFieldContext.field.label}_${signatureFieldContext.field.id}`;

    const contentControl = this.createContentControl(contentControlRange, title, this.buildTag(signatureFieldContext));

    if (signatureFieldContext.field.isParent) {
      contentControl.color = '#AA00AA';
      contentControl.insertText('\n', 'Start');
    } else {
      contentControl.insertText(`|${title}|`, 'Start');
    }

    // user can't edit it, but can't change the style either
    // contentControl.cannotEdit = true;

    await wordContext.sync();

    return contentControl;
  }

  private createContentControl(range: Word.Range, title: string, tag: string, colour?: string): Word.ContentControl {
    const contentControl = range.insertContentControl();
    contentControl.title = title;
    contentControl.tag = tag;
    contentControl.appearance = 'BoundingBox';
    // contentControl.appearance = 'Tags';
    if (colour) {
      contentControl.color = colour;
    } else {
      contentControl.color = '#FFBE50';
    }
    // without this, the placeholder text 'Click or tap here to enter text.' is stored multiple times in the glossary
    // however, it does not prevent that text being in the final document as Word replaces empty content control text
    // with the above default placeholder text
    contentControl.placeholderText = '';
    return contentControl;
  }

  private buildTag(sfc: SignatureFieldContext): string {
    return `${sfc.providerName}-${sfc.field.type}-${UidGenerator.createGuid()}`;
  }

  private subscribeToContentControlDeletedEvents(): void {
    this.contentControlMonitor.contentControlsDeleted$.pipe(concatMap((event) => this.contentControlDeleted(event.id))).subscribe();
  }

  private async contentControlDeleted(contentControlId: number): Promise<void> {
    await this.contentControlToSignatureFieldService.delete(contentControlId);
  }
}
